import React from 'react';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

/*
 * Loaded lazily, and deliberately from inside the gate rather than around it.
 * The gate itself always renders, so a lazy boundary placed around it would
 * fetch the chunk on every page; placed here, the import is only reached when
 * the weblab is on, so control-group pages never request it at all.
 *
 * Safe to mount late because the package re-syncs. SpotlightDirector dispatches
 * a `spotlight:ping` when it mounts, and every Beacon subscribes to that ping
 * and re-runs its own `spotlight:register` in response -- so a Director that
 * arrives after the Beacons still learns about all of them. Its
 * missing-registration timeout is measured from the ping rather than from page
 * load, so deferring the mount moves that window along with it instead of
 * eating into it.
 */
const SpotlightDirector = React.lazy(() =>
  import(
    /* webpackChunkName: "spotlight-assets" */ '@amzn/ads-spotlight-assets'
  ).then((module) => ({ default: module.SpotlightDirector })),
);

/**
 * Mounts the always-on SpotlightDirector orchestrator once, globally, behind the
 * ADS_SPOTLIGHT_LAUNCH_1466220 weblab (T1 = launch). The Director renders null and
 * drives spotlights via window CustomEvents + window.__SPOTLIGHT__, so it needs no
 * container or chrome context — only a single mount inside the always-rendered tree.
 */
export const SpotlightDirectorGate: React.FC = () => {
  const isSpotlightEnabled = useIsFeatureEnabled(
    WeblabedFeatures.ADS_SPOTLIGHT_LAUNCH,
  );

  return isSpotlightEnabled ? (
    <React.Suspense fallback={null}>
      <SpotlightDirector />
    </React.Suspense>
  ) : null;
};
